// InfoHint — a tiny "i" button that reveals a short description on tap.
//
// Used in page headers where the founder wants the page to read clean
// (no always-on description paragraph) but still lets a curious user
// tap to see what the page is for. Tap toggles a small popover; tap
// again, press Esc, or click outside to dismiss. Structural only —
// inherits theme colors via Tailwind utilities so all 9 themes work.
import React, { useEffect, useRef, useState } from "react";
import { Info } from "@phosphor-icons/react";

export default function InfoHint({ children, testid = "info-hint", className = "", variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // "dark" = forest button for light/standard headers.
  // "light" = cream button for colored hero headers (Kids, Christian Rap).
  const btnCls = variant === "light"
    ? "border-cream/40 text-cream/80 hover:text-cream hover:border-cream hover:bg-cream/10"
    : "border-forest/30 text-forest/70 hover:text-forest hover:border-forest hover:bg-forest/5";

  return (
    <span ref={ref} className={`relative inline-flex shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        aria-label="About this page"
        aria-expanded={open}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${btnCls}`}
      >
        <Info size={14} weight="bold" />
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid={`${testid}-panel`}
          className="hw-rise fixed left-4 right-4 top-24 z-50 mx-auto max-w-md rounded-xl border border-cream-300 bg-white shadow-xl p-3.5 text-sm text-muted_ink leading-relaxed sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-2 sm:mx-0 sm:w-72 sm:max-w-[80vw]"
        >
          {children}
        </span>
      )}
    </span>
  );
}
